import { zodResolver } from '@hookform/resolvers/zod'
import { Link as RouterLink, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { AlertErrorIcon } from '@/components/icon'
import { SimpleLogoPageContainer } from '@/components/shared/SimpleLogoPageContainer'
import {
  Box,
  Button,
  Center,
  FieldErrorText,
  FieldLabel,
  FieldRoot,
  Grid,
  Heading,
  HStack,
  Input,
  Link,
  PasswordInput,
  Stack,
  Text,
} from '@/components/ui'
import { ERROR_MESSAGES } from '@/constants/errorMessages'
import { ApiError, useMutation } from '@/lib/api'
import { signIn } from '@/lib/auth'
import { assertNever } from '@/utils/assertNever'

type Props = {
  redirectTo?: string
}

const loginFormSchema = z.object({
  email: z
    .string()
    .min(1, ERROR_MESSAGES.emailRequired)
    .email(ERROR_MESSAGES.emailFormat),
  password: z.string().min(1, ERROR_MESSAGES.passwordRequired),
})

type FormValue = z.infer<typeof loginFormSchema>

export const LoginPage = ({ redirectTo }: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValue>({
    mode: 'onBlur',
    resolver: zodResolver(loginFormSchema),
  })

  const navigate = useNavigate()

  const [apiError, setApiError] = useState<ApiError>()

  const { request: submitSignIn, isPending } = useMutation(signIn, {
    onSuccess: ({ result }) => {
      switch (result) {
        case 'done':
          navigate({
            to: redirectTo ?? '/trials',
          })
          break
        case 'requiredTOTP':
          navigate({
            to: '/mfa',
          })
          break
        case 'requiredNewPassword':
          navigate({
            to: '/register-password',
          })
          break
        default:
          return assertNever(result)
      }
    },
    onError: setApiError,
  })

  return (
    <SimpleLogoPageContainer showGradationArea>
      <Stack align="center" boxSize="full" gap="12">
        <Center flexDirection="column" gap="2">
          <Heading fontSize="xl" as="h1" fontWeight="semibold">
            ログイン
          </Heading>
          <Text>MiROHAアカウントでログインできます</Text>
        </Center>
        <Box w="full">
          <form
            onSubmit={handleSubmit(async data => {
              await submitSignIn({
                email: data.email,
                password: data.password,
              })
            })}
          >
            <Stack gap="12">
              <Grid
                rowGap="7"
                columnGap="6"
                templateColumns={{ base: '1fr', desktop: 'max-content 1fr' }}
              >
                <FieldRoot
                  display="grid"
                  alignItems="start"
                  gridTemplateColumns="subgrid"
                  gridColumn={{ base: 'span 1', desktop: 'span 2' }}
                  gap={{ base: '1', desktop: '6' }}
                  invalid={!!errors.email}
                >
                  <FieldLabel
                    fontSize="md"
                    fontWeight="normal"
                    color="text.sub"
                    h="10"
                  >
                    メールアドレス
                  </FieldLabel>
                  <Stack gap="0.5">
                    <Input
                      {...register('email')}
                      type="email"
                      placeholder="メールアドレスを入力してください"
                    />
                    <FieldErrorText>{errors.email?.message}</FieldErrorText>
                  </Stack>
                </FieldRoot>
                <FieldRoot
                  display="grid"
                  alignItems="start"
                  gridTemplateColumns="subgrid"
                  gridColumn={{ base: 'span 1', desktop: 'span 2' }}
                  gap={{ base: '1', desktop: '6' }}
                  invalid={!!errors.password}
                >
                  <FieldLabel
                    fontSize="md"
                    fontWeight="normal"
                    color="text.sub"
                    h="10"
                  >
                    パスワード
                  </FieldLabel>
                  <Stack gap="0.5">
                    <PasswordInput
                      {...register('password')}
                      autoComplete="current-password"
                      placeholder="パスワードを入力してください"
                    />
                    <FieldErrorText>{errors.password?.message}</FieldErrorText>
                  </Stack>
                </FieldRoot>
                {!!apiError && (
                  <HStack
                    gridColumn={{ base: 'span 1', desktop: 'span 2' }}
                    gap="3"
                    justify="center"
                    color="text.error"
                  >
                    <AlertErrorIcon />
                    <Text fontWeight="normal">{apiError.message}</Text>
                  </HStack>
                )}
              </Grid>
              <Center gap="7" flexDirection="column">
                <RouterLink to="/forgot-password" state={{ from: '/login' }}>
                  <Link as="span" color="text.primary" fontSize="md">
                    パスワードをお忘れの方はこちら
                  </Link>
                </RouterLink>
                <Button
                  colorPalette="blue"
                  size="xl"
                  type="submit"
                  loading={isPending}
                  w="200px"
                >
                  ログイン
                </Button>
              </Center>
            </Stack>
          </form>
        </Box>
      </Stack>
    </SimpleLogoPageContainer>
  )
}
